import {
  glasswareItems,
  GLASSWARE_BY_CATEGORY,
  type GlasswareItem,
  type GlasswareCategory,
} from "./items";

/**
 * Glassware ID - pure question-generation logic.
 *
 * Each question shows one piece of apparatus (referenced by id, the page
 * renders the SVG) and four name choices, exactly one of which is correct.
 * Distractors are drawn from the same `category` when possible so the wrong
 * answers are plausible (e.g., flask vs flask) rather than trivially wrong
 * (e.g., burette vs Bunsen burner).
 *
 * Scoring matches Speed Quiz / Reaction Race so muscle memory carries
 * across games:
 *   - correct in ≤ FAST_THRESHOLD_S : POINTS_FAST
 *   - correct (slow)                : POINTS_SLOW
 *   - wrong / timeout               : 0
 */

export const GLASSWARE_TURN_SECONDS = 12;
export const GLASSWARE_FAST_THRESHOLD_S = 4;
export const GLASSWARE_POINTS_FAST = 15;
export const GLASSWARE_POINTS_SLOW = 8;

export type GlasswareOutcome = "fast" | "slow" | "wrong" | "timeout";

export interface GlasswareQuestion {
  /** Stable id for de-duping within a session. */
  id: string;
  item: GlasswareItem;
  /** Four display labels - exactly one is correct. */
  choices: [string, string, string, string];
  correctIndex: 0 | 1 | 2 | 3;
}

function pickRandom<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

function sampleN<T>(rng: () => number, arr: T[], count: number): T[] {
  const copy = arr.slice();
  const out: T[] = [];
  while (copy.length > 0 && out.length < count) {
    const i = Math.floor(rng() * copy.length);
    out.push(copy.splice(i, 1)[0]);
  }
  return out;
}

function shuffleWithIndex<T>(
  rng: () => number,
  arr: T[],
  correctValue: T,
): { items: T[]; index: 0 | 1 | 2 | 3 } {
  const indexed = arr.map((v, i) => ({ v, i }));
  for (let i = indexed.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [indexed[i], indexed[j]] = [indexed[j], indexed[i]];
  }
  const items = indexed.map((x) => x.v);
  const idx = items.indexOf(correctValue);
  return { items, index: idx as 0 | 1 | 2 | 3 };
}

/**
 * Build a single question. Tries to honour `excludeIds`; falls back to
 * the unfiltered pool if every candidate would be excluded so long
 * sessions never deadlock.
 */
export function generateQuestion(
  rng: () => number,
  excludeIds: ReadonlySet<string>,
): GlasswareQuestion {
  const available = glasswareItems.filter((it) => !excludeIds.has(it.id));
  const pool = available.length > 0 ? available : glasswareItems;
  const target = pickRandom(rng, pool);

  // Prefer same-category distractors; backfill from the rest of the pool.
  const sameCategory = (GLASSWARE_BY_CATEGORY[target.category] ?? []).filter(
    (it) => it.id !== target.id,
  );
  const others = glasswareItems.filter(
    (it) => it.id !== target.id && it.category !== target.category,
  );
  const sameSample = sampleN(rng, sameCategory, 3);
  const distractors = sameSample.length >= 3
    ? sameSample
    : [...sameSample, ...sampleN(rng, others, 3 - sameSample.length)];

  const { items, index } = shuffleWithIndex(
    rng,
    [target.name, ...distractors.map((d) => d.name)],
    target.name,
  );

  return {
    id: target.id,
    item: target,
    choices: items as [string, string, string, string],
    correctIndex: index,
  };
}

/** Resolve an answer attempt into an awarded point value + outcome. */
export function scoreAnswer(
  chosenIndex: number | null,
  question: GlasswareQuestion,
  elapsedMs: number,
): { points: number; outcome: GlasswareOutcome } {
  if (chosenIndex === null) return { points: 0, outcome: "timeout" };
  if (chosenIndex !== question.correctIndex) return { points: 0, outcome: "wrong" };
  if (elapsedMs <= GLASSWARE_FAST_THRESHOLD_S * 1000) {
    return { points: GLASSWARE_POINTS_FAST, outcome: "fast" };
  }
  return { points: GLASSWARE_POINTS_SLOW, outcome: "slow" };
}

export type { GlasswareItem, GlasswareCategory };
